import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Plus, Phone, Mail, MessageSquare,
  Play, Pause, Copy, Trash2, Upload, MoreHorizontal, Loader2,
  Target, Users, CheckCircle, XCircle, Clock, TrendingUp,
  ChevronRight, Filter, RefreshCw, ArrowLeft, Sparkles, Send,
  BarChart3, Brain, Lightbulb, AlertTriangle, ChevronDown, ChevronUp,
  Zap, PieChart, Activity
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminCampaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [executing, setExecuting] = useState({});
  const fileInputRef = useRef(null);
  
  // Analytics states
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [zoeyQuery, setZoeyQuery] = useState("");
  const [zoeyResponse, setZoeyResponse] = useState("");
  const [zoeyLoading, setZoeyLoading] = useState(false);
  const [zoeyConversation, setZoeyConversation] = useState([]);
  const chatEndRef = useRef(null);

  // Form states
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    description: "",
    action_type: "call",
    daily_limit: 50,
    tags: []
  });
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicateAction, setDuplicateAction] = useState("");

  const adminEmail = localStorage.getItem("admin_email");

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/api/admin/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      toast.error("Failed to fetch campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name.trim()) {
      toast.error("Campaign name is required");
      return;
    }

    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/api/admin/campaigns`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(newCampaign)
      });

      if (response.ok) {
        toast.success("Campaign created successfully");
        setShowCreateModal(false);
        setNewCampaign({ name: "", description: "", action_type: "call", daily_limit: 50, tags: [] });
        fetchCampaigns();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to create campaign");
      }
    } catch (error) {
      toast.error("Failed to create campaign");
    }
  };

  const handleDuplicateCampaign = async () => {
    if (!duplicateName.trim()) {
      toast.error("New campaign name is required");
      return;
    }

    const token = localStorage.getItem("admin_token");
    try {
      const url = new URL(`${API}/api/admin/campaigns/${selectedCampaign.campaign_id}/duplicate`);
      url.searchParams.append("new_name", duplicateName);
      if (duplicateAction) {
        url.searchParams.append("new_action_type", duplicateAction);
      }

      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Campaign duplicated successfully");
        setShowDuplicateModal(false);
        setDuplicateName("");
        setDuplicateAction("");
        fetchCampaigns();
      }
    } catch (error) {
      toast.error("Failed to duplicate campaign");
    }
  };

  const handleUploadLeads = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCampaign) return;

    const token = localStorage.getItem("admin_token");
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(
        `${API}/api/admin/campaigns/${selectedCampaign.campaign_id}/upload-leads`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`Uploaded ${data.leads_created} leads (${data.leads_skipped} skipped)`);
        setShowUploadModal(false);
        fetchCampaigns();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      toast.error("Upload failed");
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleExecuteAll = async (campaign) => {
    setExecuting(prev => ({ ...prev, [campaign.campaign_id]: true }));
    const token = localStorage.getItem("admin_token");

    try {
      const response = await fetch(
        `${API}/api/admin/campaigns/${campaign.campaign_id}/execute-all`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`Campaign started: ${data.leads_queued} leads queued`);
        fetchCampaigns();
      }
    } catch (error) {
      toast.error("Failed to start campaign");
    } finally {
      setExecuting(prev => ({ ...prev, [campaign.campaign_id]: false }));
    }
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!window.confirm(`Delete campaign "${campaign.name}"?`)) return;

    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(
        `${API}/api/admin/campaigns/${campaign.campaign_id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success("Campaign deleted");
        fetchCampaigns();
      }
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const getActionIcon = (type) => {
    switch (type) {
      case "call": return <Phone className="w-4 h-4" />;
      case "sms": return <MessageSquare className="w-4 h-4" />;
      case "email": return <Mail className="w-4 h-4" />;
      default: return <Target className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "paused": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "completed": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      default: return "bg-zinc-500/20 text-zinc-400 border-zinc-500/30";
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/login");
  };

  // Fetch analytics summary
  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    const token = localStorage.getItem("admin_token");
    try {
      const [summaryRes, recsRes] = await Promise.all([
        fetch(`${API}/api/admin/campaigns/analytics/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/api/admin/campaigns/analytics/recommendations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setAnalyticsData(data);
      }
      
      if (recsRes.ok) {
        const data = await recsRes.json();
        setRecommendations(data.recommendations || []);
      }
    } catch (error) {
      toast.error("Failed to fetch analytics");
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Ask ZOEY for campaign insights
  const askZoey = async () => {
    if (!zoeyQuery.trim()) return;
    
    setZoeyLoading(true);
    const token = localStorage.getItem("admin_token");
    
    // Add user message to conversation
    const newUserMessage = { role: "user", content: zoeyQuery };
    setZoeyConversation(prev => [...prev, newUserMessage]);
    setZoeyQuery("");
    
    try {
      const response = await fetch(`${API}/api/admin/campaigns/analytics/zoey`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          query: zoeyQuery,
          conversation_history: zoeyConversation
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const assistantMessage = { role: "assistant", content: data.response };
        setZoeyConversation(prev => [...prev, assistantMessage]);
        setZoeyResponse(data.response);
      } else {
        toast.error("Failed to get ZOEY response");
      }
    } catch (error) {
      toast.error("Error communicating with ZOEY");
    } finally {
      setZoeyLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };

  // Toggle analytics panel
  const toggleAnalytics = () => {
    if (!showAnalytics) {
      fetchAnalytics();
    }
    setShowAnalytics(!showAnalytics);
  };

  // Quick insight questions
  const quickQuestions = [
    "Which campaign type performs best?",
    "What's my overall conversion rate?",
    "How can I improve bounce rates?",
    "When should I run campaigns?"
  ];

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      {/* CRM Style Header */}
      <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto">
        {/* Back Navigation */}
        <button
          onClick={() => navigate("/admin/crm")}
          className="flex items-center gap-2 mb-6 text-zinc-500 hover:text-[#14A800] transition-colors group"
          data-testid="back-to-crm"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm">Back to CRM</span>
        </button>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold font-[Manrope] text-white flex items-center gap-3">
              <Send className="w-8 h-8 text-purple-400" />
              Marketing Campaigns
            </h1>
            <p className="text-zinc-500 mt-2">Manage outbound calls, SMS, and email campaigns</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={toggleAnalytics}
              className={`border-white/10 text-white hover:bg-white/10 ${showAnalytics ? 'bg-purple-500/20 border-purple-500/30' : 'bg-white/5'}`}
              data-testid="analytics-btn"
            >
              <Brain className="w-4 h-4 mr-2" />
              ZOEY Analytics
            </Button>
            <Button
              variant="outline"
              onClick={fetchCampaigns}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#14A800] hover:bg-[#128A00] text-white"
              data-testid="create-campaign-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </div>
        </div>

        {/* ZOEY Analytics Panel */}
        {showAnalytics && (
          <div className="mb-8 space-y-6">
            {/* Analytics Summary Cards */}
            {loadingAnalytics ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
              </div>
            ) : analyticsData && (
              <>
                {/* Summary Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-purple-400 text-sm mb-1">
                      <Target className="w-4 h-4" />
                      Total Campaigns
                    </div>
                    <div className="text-3xl font-bold text-white">{analyticsData.total_campaigns}</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-blue-400 text-sm mb-1">
                      <Users className="w-4 h-4" />
                      Total Leads
                    </div>
                    <div className="text-3xl font-bold text-white">{analyticsData.total_leads}</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
                      <TrendingUp className="w-4 h-4" />
                      Conversion Rate
                    </div>
                    <div className="text-3xl font-bold text-white">{analyticsData.overall_metrics?.conversion_rate || 0}%</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-amber-400 text-sm mb-1">
                      <Activity className="w-4 h-4" />
                      Contact Rate
                    </div>
                    <div className="text-3xl font-bold text-white">{analyticsData.overall_metrics?.contact_rate || 0}%</div>
                  </div>
                </div>

                {/* Channel Performance & Recommendations */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Channel Performance */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                      <PieChart className="w-5 h-5 text-purple-400" />
                      Channel Performance
                    </h3>
                    <div className="space-y-3">
                      {Object.entries(analyticsData.by_action_type || {}).map(([type, data]) => (
                        <div key={type} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${
                              type === "call" ? "bg-blue-500/20 text-blue-400" :
                              type === "sms" ? "bg-purple-500/20 text-purple-400" :
                              "bg-amber-500/20 text-amber-400"
                            }`}>
                              {type === "call" ? <Phone className="w-4 h-4" /> :
                               type === "sms" ? <MessageSquare className="w-4 h-4" /> :
                               <Mail className="w-4 h-4" />}
                            </div>
                            <div>
                              <div className="text-white font-medium capitalize">{type}</div>
                              <div className="text-xs text-zinc-500">{data.campaigns} campaigns, {data.leads} leads</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-white font-semibold">{data.conversion_rate}%</div>
                            <div className="text-xs text-zinc-500">conversion</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {analyticsData.performance?.best_action_type && (
                      <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <div className="flex items-center gap-2 text-green-400 text-sm">
                          <Zap className="w-4 h-4" />
                          <span>Best performing: <strong className="uppercase">{analyticsData.performance.best_action_type}</strong> at {analyticsData.performance.best_action_conversion}%</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* AI Recommendations */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                      <Lightbulb className="w-5 h-5 text-amber-400" />
                      AI Recommendations
                    </h3>
                    <div className="space-y-3 max-h-[200px] overflow-y-auto">
                      {recommendations.length > 0 ? recommendations.map((rec, idx) => (
                        <div key={idx} className={`p-3 rounded-lg border ${
                          rec.priority === "high" ? "bg-red-500/10 border-red-500/20" :
                          rec.priority === "medium" ? "bg-amber-500/10 border-amber-500/20" :
                          rec.priority === "info" ? "bg-blue-500/10 border-blue-500/20" :
                          "bg-zinc-500/10 border-zinc-500/20"
                        }`}>
                          <div className="flex items-start gap-2">
                            {rec.icon === "alert" ? <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5" /> :
                             rec.icon === "play" ? <Play className="w-4 h-4 text-green-400 mt-0.5" /> :
                             rec.icon === "clock" ? <Clock className="w-4 h-4 text-amber-400 mt-0.5" /> :
                             rec.icon === "trending-up" ? <TrendingUp className="w-4 h-4 text-blue-400 mt-0.5" /> :
                             rec.icon === "lightbulb" ? <Lightbulb className="w-4 h-4 text-amber-400 mt-0.5" /> :
                             <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />}
                            <span className="text-sm text-zinc-300">{rec.message}</span>
                          </div>
                        </div>
                      )) : (
                        <div className="text-zinc-500 text-sm">No recommendations yet. Run some campaigns to get insights!</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ZOEY Chat Interface */}
                <div className="bg-gradient-to-br from-purple-500/5 to-blue-500/5 border border-purple-500/20 rounded-xl p-5">
                  <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                    <Brain className="w-5 h-5 text-purple-400" />
                    Ask ZOEY - Campaign Analytics AI
                  </h3>
                  
                  {/* Quick Questions */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {quickQuestions.map((q, idx) => (
                      <button
                        key={idx}
                        onClick={() => { setZoeyQuery(q); }}
                        className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-zinc-400 hover:text-white transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                  
                  {/* Conversation History */}
                  {zoeyConversation.length > 0 && (
                    <div className="mb-4 max-h-[300px] overflow-y-auto space-y-3 p-3 bg-black/30 rounded-lg">
                      {zoeyConversation.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] p-3 rounded-lg ${
                            msg.role === "user" 
                              ? "bg-[#14A800]/20 text-white" 
                              : "bg-purple-500/10 border border-purple-500/20 text-zinc-300"
                          }`}>
                            {msg.role === "assistant" && (
                              <div className="flex items-center gap-2 text-purple-400 text-xs mb-2">
                                <Sparkles className="w-3 h-3" />
                                ZOEY
                              </div>
                            )}
                            <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                          </div>
                        </div>
                      ))}
                      {zoeyLoading && (
                        <div className="flex justify-start">
                          <div className="bg-purple-500/10 border border-purple-500/20 p-3 rounded-lg">
                            <div className="flex items-center gap-2 text-purple-400">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span className="text-sm">ZOEY is analyzing...</span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>
                  )}
                  
                  {/* Input */}
                  <div className="flex gap-2">
                    <Input
                      value={zoeyQuery}
                      onChange={(e) => setZoeyQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && askZoey()}
                      placeholder="Ask about campaign performance, optimization tips, best times to reach leads..."
                      className="flex-1 bg-black/50 border-white/10 text-white"
                      data-testid="zoey-analytics-input"
                    />
                    <Button
                      onClick={askZoey}
                      disabled={zoeyLoading || !zoeyQuery.trim()}
                      className="bg-purple-500 hover:bg-purple-600 text-white"
                    >
                      {zoeyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                  
                  {zoeyConversation.length > 0 && (
                    <button
                      onClick={() => setZoeyConversation([])}
                      className="mt-2 text-xs text-zinc-500 hover:text-zinc-400"
                    >
                      Clear conversation
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Campaigns Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#14A800]" />
          </div>
        ) : campaigns.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
            <Target className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-white text-lg font-semibold mb-2">No Campaigns Yet</h3>
            <p className="text-zinc-500 mb-4">Create your first marketing campaign to get started</p>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#14A800] hover:bg-[#128A00] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {campaigns.map((campaign) => (
              <div
                key={campaign.campaign_id}
                className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-white/20 transition-all"
              >
                {/* Campaign Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      campaign.action_type === "call" ? "bg-blue-500/20 text-blue-400" :
                      campaign.action_type === "sms" ? "bg-purple-500/20 text-purple-400" :
                      "bg-amber-500/20 text-amber-400"
                    }`}>
                      {getActionIcon(campaign.action_type)}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{campaign.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${getStatusColor(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-[#666] hover:text-white" data-testid={`campaign-menu-${campaign.campaign_id}`}>
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#111] border-[#333]">
                      <DropdownMenuItem
                        onClick={() => { setSelectedCampaign(campaign); setShowUploadModal(true); }}
                        className="text-[#888] focus:text-white focus:bg-[#222]"
                        data-testid={`upload-leads-${campaign.campaign_id}`}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Leads
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedCampaign(campaign);
                          setDuplicateName(`${campaign.name} (Copy)`);
                          setDuplicateAction(campaign.action_type);
                          setShowDuplicateModal(true);
                        }}
                        className="text-[#888] focus:text-white focus:bg-[#222]"
                        data-testid={`duplicate-campaign-${campaign.campaign_id}`}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#333]" />
                      <DropdownMenuItem
                        onClick={() => handleDeleteCampaign(campaign)}
                        className="text-red-400 focus:text-red-400 focus:bg-red-400/10"
                        data-testid={`delete-campaign-${campaign.campaign_id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-white">{campaign.metrics?.total_leads || 0}</div>
                    <div className="text-xs text-zinc-500">Total</div>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-400">{campaign.metrics?.success || 0}</div>
                    <div className="text-xs text-zinc-500">Success</div>
                  </div>
                  <div className="bg-black/30 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-amber-400">{campaign.metrics?.pending || 0}</div>
                    <div className="text-xs text-zinc-500">Pending</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-zinc-500 mb-1">
                    <span>Progress</span>
                    <span>
                      {campaign.metrics?.total_leads > 0
                        ? Math.round(((campaign.metrics?.contacted || 0) / campaign.metrics.total_leads) * 100)
                        : 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#14A800] transition-all"
                      style={{
                        width: `${campaign.metrics?.total_leads > 0
                          ? ((campaign.metrics?.contacted || 0) / campaign.metrics.total_leads) * 100
                          : 0}%`
                      }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setSelectedCampaign(campaign); setShowUploadModal(true); }}
                    className="flex-1 bg-white/5 border-white/10 text-white hover:bg-white/10"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleExecuteAll(campaign)}
                    disabled={executing[campaign.campaign_id] || (campaign.metrics?.pending || 0) === 0}
                    className="flex-1 bg-[#14A800] hover:bg-[#128A00] text-white disabled:opacity-50"
                  >
                    {executing[campaign.campaign_id] ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <Play className="w-4 h-4 mr-1" />
                    )}
                    Run All
                  </Button>
                </div>

                {/* Daily Limit Info */}
                <div className="mt-3 text-xs text-zinc-500 text-center">
                  Daily limit: {campaign.daily_limit} {campaign.action_type}s
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Campaign</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Set up a new marketing campaign for outreach
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Campaign Name</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., HVAC Outbound San Diego"
                className="bg-black/50 border-white/10 text-white mt-1"
                data-testid="campaign-name-input"
              />
            </div>

            <div>
              <Label className="text-zinc-400">Description (Optional)</Label>
              <Input
                value={newCampaign.description}
                onChange={(e) => setNewCampaign(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Campaign description..."
                className="bg-black/50 border-white/10 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-zinc-400">Action Type</Label>
              <Select
                value={newCampaign.action_type}
                onValueChange={(value) => setNewCampaign(prev => ({ ...prev, action_type: value }))}
              >
                <SelectTrigger className="bg-black/50 border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="call" className="text-white focus:bg-white/10">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Phone Call (Retell)
                    </div>
                  </SelectItem>
                  <SelectItem value="sms" className="text-white focus:bg-white/10">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> SMS (Retell)
                    </div>
                  </SelectItem>
                  <SelectItem value="email" className="text-white focus:bg-white/10">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email (Postmark)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400">Daily Limit</Label>
              <Select
                value={String(newCampaign.daily_limit)}
                onValueChange={(value) => setNewCampaign(prev => ({ ...prev, daily_limit: parseInt(value) }))}
              >
                <SelectTrigger className="bg-black/50 border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {[25, 50, 75, 100, 125, 150, 175, 200].map(limit => (
                    <SelectItem key={limit} value={String(limit)} className="text-white focus:bg-white/10">
                      {limit} per day
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)} className="text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleCreateCampaign} className="bg-[#14A800] hover:bg-[#128A00]">
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Leads Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Leads</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Upload a CSV or Excel file with leads for "{selectedCampaign?.name}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center">
              <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
              <p className="text-zinc-400 mb-2">Drag & drop or click to upload</p>
              <p className="text-xs text-zinc-600 mb-4">CSV or Excel (.xlsx) files</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleUploadLeads}
                className="hidden"
                id="lead-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="bg-white/5 border-white/10 text-white"
              >
                Select File
              </Button>
            </div>
            
            <div className="bg-black/30 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-white mb-2">Required Columns:</h4>
              <ul className="text-xs text-zinc-500 space-y-1">
                <li>• email OR phone (at least one)</li>
                <li>• first_name, last_name (optional)</li>
                <li>• company_name, job_title (optional)</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Campaign Modal */}
      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Duplicate Campaign</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Create a copy of "{selectedCampaign?.name}" with same leads
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">New Campaign Name</Label>
              <Input
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                placeholder="Enter new campaign name"
                className="bg-black/50 border-white/10 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-zinc-400">Action Type</Label>
              <Select value={duplicateAction} onValueChange={setDuplicateAction}>
                <SelectTrigger className="bg-black/50 border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  <SelectItem value="call" className="text-white focus:bg-white/10">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Phone Call
                    </div>
                  </SelectItem>
                  <SelectItem value="sms" className="text-white focus:bg-white/10">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> SMS
                    </div>
                  </SelectItem>
                  <SelectItem value="email" className="text-white focus:bg-white/10">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-600 mt-1">
                Change action to target same leads with different channel
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDuplicateModal(false)} className="text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleDuplicateCampaign} className="bg-[#14A800] hover:bg-[#128A00]">
              <Copy className="w-4 h-4 mr-2" />
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
